import { useTranslation } from 'react-i18next';
/* eslint-disable no-param-reassign */
import { Coords } from 'google-map-react';
import moment from 'moment';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AVLCredentials, getIturanLocation, AVLData } from 'src/api/api';
import { IturanLocationRequestPayload, IturanRecord } from 'src/api/types';
import { lineSelector } from 'src/store/selectores/linesSelectores';
import { GpsResponseEnum, ILine } from 'src/types/line';
import { getAsTemplateStrArr as asTSA } from 'src/utilis/utilis';
import { getRecordsJSON, specialCharsRegex } from '../../LineMap/utils';
import { setLastTimeCalledIturanAction } from 'src/store/slices/common/commonSlice';
import { useRootAppSelector } from 'src/store/hooks';

function isTimeDifferenceGreaterThan1Second(timeStr1: string, timeStr2: string) {
    // Parse the time strings into Date objects
    const time1 = new Date(`1970-01-01T${timeStr1}`);
    const time2 = new Date(`1970-01-01T${timeStr2}`);

    // Calculate the time difference in milliseconds
    const timeDifference = Math.abs(time1.getTime() - time2.getTime());

    // Check if the time difference is more than 1 second (1000 milliseconds)
    return timeDifference > 1000;
}
export interface AVLCreds {
    userName: string;
    Password: string;
}
export interface IturanResponse {
    status: number;
    data: {
        body: string;
        statusCode: number;
    };
}
// & TYPES
export interface IMarkers extends Coords {
    deg?: string;
    date?: Date;
    id?: number;
}
interface Args {
    avlData: AVLData;
    driverPath: IMarkers[];
    setDriverPosition: (newVal: IMarkers | undefined) => void;
    setMarkers: (newVal: IMarkers[]) => void;
    setCenter: (newVal: Coords) => void;
    setIsLoading: (newVal: boolean) => void;
    setIturanStatusMessage: (newVal: string) => void;
}
export enum AVLTypeEnum {
    Default = '0',
    Ituran = '1',
}
const VALID_CODES_FOR_SHOWING_LOCATION = [0, 2, 3, 4];
const VALID_RECORDS_COUNT = 3;
const DEFAULT_UAID = 0;
const MAX_NUMBER_OF_RECORDS = 10000;

export const shouldUseIturanLocation = (data: {
    // gpsData: unknown[];
    lineCarNumber: string;
    AVLType?: AVLTypeEnum | undefined;
    // responseCode: GpsResponseEnum;
}): boolean => {
    const { lineCarNumber, AVLType } = data;

    if (!AVLType) return false;

    if (
        // gpsData.length < VALID_RECORDS_COUNT &&
        lineCarNumber &&
        AVLType === AVLTypeEnum.Ituran
        // VALID_CODES_FOR_SHOWING_LOCATION.includes(responseCode)
    )
        return true;
    return false;
};

export const getIturanRecords: (payload: IturanLocationRequestPayload) => Promise<{
    status: number | string;
    records: IturanRecord[];
    error: string;
}> = async (payload: IturanLocationRequestPayload) => {
    const { data, status }: IturanResponse = await getIturanLocation(payload);

    let records: IturanRecord[] = [];
    let error = '';
    if (status === 200 && data.statusCode === 200) {
        const recordsJsonResult = getRecordsJSON(data.body);
        if (Array.isArray(recordsJsonResult)) {
            records = recordsJsonResult;
        } else {
            error = recordsJsonResult;
        }
    }

    const getStatusCode = () => {
        if (status !== 200) return status;
        if (data.statusCode !== 200) return data.statusCode;
        if (error) return error;
        return 200;
    };
    return { status: getStatusCode(), records, error };
};

const buildReqPayload = (selectedLine: ILine | undefined, AVLCreds: AVLCredentials) => {
    if (!selectedLine) {
        return null;
    }

    const { carNumber, startTime, endTime } = selectedLine;

    const ituranCallPayload: IturanLocationRequestPayload = {
        UAID: DEFAULT_UAID,
        MaxNumberOfRecords: MAX_NUMBER_OF_RECORDS,
        Plate: carNumber.replaceAll(specialCharsRegex, ''),
        Start: moment(startTime).toISOString(),
        End: moment(endTime).toISOString(),
        userName: AVLCreds.AVLUser,
        Password: AVLCreds.AVLPass,
    };

    return ituranCallPayload;
};
export const defaultCenter: Coords = {
    lat: 31.896997821344108,
    lng: 34.80000972747803,
};
export const getCenter: (driverPath: IMarkers[]) => Coords = (driverPath) => {
    const lastGpsLocationIndex = driverPath.length - 1;

    return driverPath.length ? driverPath[lastGpsLocationIndex] : defaultCenter;
};
export enum ReqIturanResult {
    OK = 1,
    Error = 2,
    LowQualityResult = 3,
}
// -- Hook
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function useIturanData() {
    const selectedLine = useSelector((state) => lineSelector(state));
    const lastTimeCalledIturan = useRootAppSelector((state) => state.commonSlice.lastTimeCalledIturan);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const reqGetIturanData = async (AVLCreds: AVLCredentials) => {
        const ituranCallPayload = buildReqPayload(selectedLine, AVLCreds);

        if (!ituranCallPayload) {
            return null;
        }
        const currentTime = moment();

        if (lastTimeCalledIturan) {
            const temp = isTimeDifferenceGreaterThan1Second(
                lastTimeCalledIturan,
                currentTime.format('HH:mm:ss')
            );

            if (!temp) return;
        }
        dispatch(setLastTimeCalledIturanAction(currentTime.format('HH:mm:ss')));

        const result = await getIturanRecords(ituranCallPayload);
        return result;
    };

    const handleIturanReq = async ({
        avlData,
        driverPath,
        setDriverPosition,
        setMarkers,
        setCenter,
        setIsLoading,
        setIturanStatusMessage,
    }: Args): Promise<ReqIturanResult> => {
        const { AVLUser, AVLPass } = avlData;

        const ituranData = await reqGetIturanData({
            AVLUser,
            AVLPass,
        });

        if (!ituranData) return ReqIturanResult.Error;

        const { status: ituranStatus, records: ituranGpsRecords, error } = ituranData;

        if (ituranStatus === 200) {
            if (ituranGpsRecords.length) {
                driverPath = ituranGpsRecords.map((rec) => {
                    return {
                        lat: rec.Lat,
                        lng: rec.Lon,
                        date: rec.Date,
                    };
                });
                const lastLocation = ituranGpsRecords[ituranGpsRecords.length - 1];
                setDriverPosition({
                    lat: lastLocation.Lat,
                    lng: lastLocation.Lon,
                    deg: lastLocation.Head,
                    date: lastLocation.Date,
                });
            } else {
                return ReqIturanResult.LowQualityResult;
            }

            setMarkers(driverPath);
            setCenter(getCenter(driverPath));
            setIsLoading(false);
            return ReqIturanResult.OK;
        }

        setIturanStatusMessage(`${t(asTSA('thereIsNoLocationDataForThisLine'))}`);
        console.log({ ituranError: error });
        return ReqIturanResult.Error;
    };

    return { handleIturanReq };
}

// const payload: IturanLocationRequestPayload = {
//    userName: '==gM0lWW',
//    Password: 'zUTMyUTM',
//    Plate: '54046101',
//    Start: '2022-02-23T12:00',
//    End: '2022-02-23T16:00',
//    UAID: 0,
//    MaxNumberOfRecords: 5,
// };

// console.log(
//    getRecordsJSON(
//       '"<?xml version="1.0" encoding="utf-8"?>\r\n<string xmlns="http://www.ituran.com/ituranWebService3">{"ReturnCode":"OK","Username":"Yit2","Plates":["54046101"],"StartTime":"2022-02-23T12:00:00","EndTime":"2022-02-23T16:00:00","Records":[{"Plate":"54046101","Lat":31.652596000000003,"Lon":35.186103999999993,"Address":"3157, ישראל","Date":"2022-02-23T12:03:00","DateTimeUTC":"2022-02-23T10:03:00","DateTimeFormated":null,"Speed":"0","Head":"2","Ecm":0,"Mileage":298976.0,"StatusData":null,"Id":3106763730830,"Rules":null,"HighResolutionId":0,"StrDate":"2022-02-23 12:03:00","Pocsag":1191262268,"PlatformId":2418550,"PocsagStr":"60.56.1.71","DriverIdentification":{"DriverName":"שאדי זואהרה 4287","DriverKey":328026471,"DriverTZ":"328026471","DriverType":2},"Areas":[],"Attribute":null,"Attribute1":"","Attribute2":"","KnownData":true},{"Plate":"54046101","Lat":31.652596000000003,"Lon":35.186103999999993,"Address":"3157, ישראל","Date":"2022-02-23T12:13:00","DateTimeUTC":"2022-02-23T10:13:00","DateTimeFormated":null,"Speed":"0","Head":"2","Ecm":0,"Mileage":298976.0,"StatusData":null,"Id":3106784606760,"Rules":null,"HighResolutionId":0,"StrDate":"2022-02-23 12:13:00","Pocsag":1191262268,"PlatformId":2418550,"PocsagStr":"60.56.1.71","DriverIdentification":{"DriverName":"שאדי זואהרה 4287","DriverKey":328026471,"DriverTZ":"328026471","DriverType":2},"Areas":[],"Attribute":null,"Attribute1":"","Attribute2":"","KnownData":true},{"Plate":"54046101","Lat":31.652596000000003,"Lon":35.186103999999993,"Address":"3157, ישראל","Date":"2022-02-23T12:22:59","DateTimeUTC":"2022-02-23T10:22:59.567","DateTimeFormated":null,"Speed":"0","Head":"2","Ecm":0,"Mileage":298976.0,"StatusData":null,"Id":3106805544810,"Rules":null,"HighResolutionId":0,"StrDate":"2022-02-23 12:22:59","Pocsag":1191262268,"PlatformId":2418550,"PocsagStr":"60.56.1.71","DriverIdentification":{"DriverName":"שאדי זואהרה 4287","DriverKey":328026471,"DriverTZ":"328026471","DriverType":2},"Areas":[],"Attribute":null,"Attribute1":"","Attribute2":"","KnownData":true},{"Plate":"54046101","Lat":31.652596000000003,"Lon":35.186103999999993,"Address":"3157, ישראל","Date":"2022-02-23T12:33:00","DateTimeUTC":"2022-02-23T10:33:00","DateTimeFormated":null,"Speed":"0","Head":"2","Ecm":0,"Mileage":298976.0,"StatusData":null,"Id":3106826505510,"Rules":null,"HighResolutionId":0,"StrDate":"2022-02-23 12:33:00","Pocsag":1191262268,"PlatformId":2418550,"PocsagStr":"60.56.1.71","DriverIdentification":{"DriverName":"שאדי זואהרה 4287","DriverKey":328026471,"DriverTZ":"328026471","DriverType":2},"Areas":[],"Attribute":null,"Attribute1":"","Attribute2":"","KnownData":true},{"Plate":"54046101","Lat":31.652596000000003,"Lon":35.186103999999993,"Address":"3157, ישראל","Date":"2022-02-23T12:42:59","DateTimeUTC":"2022-02-23T10:42:59.293","DateTimeFormated":null,"Speed":"0","Head":"2","Ecm":0,"Mileage":298976.0,"StatusData":null,"Id":3106847697000,"Rules":null,"HighResolutionId":0,"StrDate":"2022-02-23 12:42:59","Pocsag":1191262268,"PlatformId":2418550,"PocsagStr":"60.56.1.71","DriverIdentification":{"DriverName":"שאדי זואהרה 4287","DriverKey":328026471,"DriverTZ":"328026471","DriverType":2},"Areas":[],"Attribute":null,"Attribute1":"","Attribute2":"","KnownData":true}],"NumOfRecords":5}</string>"}',
//    ),
// );

// const foo = async () => {
//    const result = await getIturanData(payload);
//    console.log(result);
// };

// foo();
// // console.log(decodeAVLCredStr('=cjN1QzMyETc'));
// export const decodeAVLCreds = (
//    encodedUserName: string,
//    encodedPassword: string,
// ): AVLCreds => {
//    const decoded = {
//       userName: decodeAVLCredStr(encodedUserName),
//       Password: decodeAVLCredStr(encodedPassword),
//    };
//    console.log({ decoded }, encodedUserName, encodedPassword);
//    return decoded;
// };
// const decodeAVLCredStr = (encodedStr: string): string => {
//    return atob(encodedStr.split('').reverse().join(''));
// };
